// Activity tab of the Schedule Review side panel (UI Phase 1): a flat audit log of all
// settled moves, newest first, grouped by day. Recreates the PR #6802 prototype ActivityList
// on top of the CardGroup/Card common components, fed by mock data until GraphQL lands.
// Deferred from the prototype: swap moves, Undo, dynamic scope line.
import React, { useMemo, useState } from 'react';
import {
  CardGroup,
  Gap,
  Gutter,
  Icon,
  Input,
  Select,
  Text,
} from '@common/components';
import type {
  ScheduleDayType,
  ScheduleMoveSourceFilterType,
  ScheduleMoveType,
  ScheduleShiftType,
  ScheduleShiftTypeSectionType,
} from '@load-balancing/types';
import { ScheduleReviewActivityTabCard } from './_schedule-review-activity-tab-card';
import {
  buildShiftLineMap,
  filterMovesBySource,
  filterMovesByStaff,
  getGridlessMoveIds,
  groupMovesByDay,
  sortMovesNewestFirst,
} from './_schedule-review-activity-tab-service';

const SOURCE_FILTER_OPTIONS: {
  value: ScheduleMoveSourceFilterType;
  label: string;
}[] = [
  { value: 'all', label: 'All changes' },
  { value: 'system', label: 'Works' },
  { value: 'manual', label: 'Manual' },
];

export function ScheduleReviewActivityTab({
  moves,
  shiftsByWeek,
  daysByWeek,
  shiftTypeSections,
  selectedMoveId,
  onMoveSelect,
  sourceFilter,
  onSourceFilterChange,
}: {
  moves: ScheduleMoveType[];
  shiftsByWeek: Record<string, ScheduleShiftType[]>;
  daysByWeek: Record<string, ScheduleDayType[]>;
  shiftTypeSections: ScheduleShiftTypeSectionType[];
  selectedMoveId: string | null;
  onMoveSelect: (moveId: string | null) => void;
  /** Controlled source filter — "View auto-applied changes" sets it to 'system' (Works). */
  sourceFilter: ScheduleMoveSourceFilterType;
  onSourceFilterChange: (value: ScheduleMoveSourceFilterType) => void;
}) {
  const [staffFilter, setStaffFilter] = useState('');

  const shiftLineById = useMemo(
    () => buildShiftLineMap(shiftsByWeek, daysByWeek, shiftTypeSections),
    [shiftsByWeek, daysByWeek, shiftTypeSections]
  );

  const sortedMoves = sortMovesNewestFirst(moves);
  const filteredMoves = filterMovesByStaff(
    filterMovesBySource(sortedMoves, sourceFilter),
    staffFilter
  );
  const groups = groupMovesByDay(filteredMoves);
  const disabledKeys = getGridlessMoveIds(filteredMoves);

  if (moves.length === 0) {
    return (
      <Gutter asPadding size="md" className="schedule-review-activity-tab">
        <EmptyState />
      </Gutter>
    );
  }

  return (
    <Gutter asPadding size="md" className="schedule-review-activity-tab">
      <Gap vertical size="sm" fullWidth>
        <Gap size="sm" align="center" justify="space-between" fullWidth>
          <Text
            size="sm"
            weight="medium"
            color="midnightRegular"
            transform="uppercase"
            letterSpacing="xs"
          >
            All weeks ∙ All roles
          </Text>
          <Select<ScheduleMoveSourceFilterType>
            size="small"
            value={sourceFilter}
            onChange={onSourceFilterChange}
            dropdownMatchSelectWidth={false}
            options={SOURCE_FILTER_OPTIONS}
          />
        </Gap>

        <Input
          prefix={
            <Icon
              icon="search"
              kind="light"
              color="midnightLighter"
              size="sm"
            />
          }
          allowClear
          placeholder="Search staff"
          value={staffFilter}
          onChange={(event) => setStaffFilter(event.target.value)}
        />

        {groups.length === 0 ? (
          <EmptyState staffFilter={staffFilter} />
        ) : (
          groups.map((group) => (
            <div key={group.label} style={{ width: '100%' }}>
              <Gutter direction="vertical" size="sm">
                <Text
                  size="sm"
                  weight="medium"
                  secondary
                  transform="uppercase"
                  letterSpacing="xs"
                >
                  {group.label}
                </Text>
              </Gutter>
              <CardGroup
                variant="joined"
                selectedId={selectedMoveId}
                onSelect={onMoveSelect}
                disabledKeys={disabledKeys}
              >
                {group.moves.map((move) => (
                  <ScheduleReviewActivityTabCard
                    key={move.id}
                    move={move}
                    shiftLineById={shiftLineById}
                  />
                ))}
              </CardGroup>
            </div>
          ))
        )}
      </Gap>
    </Gutter>
  );
}

function EmptyState({ staffFilter = '' }: { staffFilter?: string }) {
  const query = staffFilter.trim();
  const primary = query ? `No results for "${query}"` : 'No activity yet';
  const secondary = query
    ? 'Try a different name or clear the search'
    : 'All schedule changes will appear here';

  return (
    <Gutter asPadding size="md" direction="vertical">
      <Gap vertical size="xs" fullWidth>
        <Text size="lg" weight="medium" color="midnightRegular">
          {primary}
        </Text>
        <Text size="md" secondary>
          {secondary}
        </Text>
      </Gap>
    </Gutter>
  );
}
